import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  BeakerIcon,
  FireIcon,
  CubeIcon,
  SparklesIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { FeedStockNutritionalInfo } from '../api/feedStock';
import { format } from 'date-fns';

interface NutritionalInfoDisplayProps {
  nutritionalInfo: FeedStockNutritionalInfo;
  className?: string;
  showAnalysisMetadata?: boolean;
}

export default function NutritionalInfoDisplay({
  nutritionalInfo,
  className = '',
  showAnalysisMetadata = true
}: NutritionalInfoDisplayProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['macronutrients']));

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatValue = (value: number | undefined, unit: string = '%', decimals: number = 1): string => {
    if (value === undefined || value === null) return 'Not specified';
    return `${value.toFixed(decimals)}${unit}`;
  };

  const getCompletionPercentage = (section: string): number => {
    const sectionFields = {
      macronutrients: ['protein', 'fat', 'fiber', 'carbohydrate', 'moisture', 'calories'],
      majorMinerals: ['calcium', 'phosphorus', 'magnesium', 'potassium', 'sodium'],
      traceMinerals: ['iron', 'zinc', 'manganese', 'copper', 'selenium'],
      fatSolubleVitamins: ['vitaminA', 'vitaminD3', 'vitaminE', 'vitaminK'],
      waterSolubleVitamins: ['thiamine', 'riboflavin', 'niacin', 'pantothenicAcid', 'pyridoxine', 'biotin', 'folicAcid', 'vitaminB12', 'choline']
    };

    const fields = sectionFields[section as keyof typeof sectionFields] || [];
    const completedFields = fields.filter(field => 
      nutritionalInfo[field as keyof FeedStockNutritionalInfo] !== undefined &&
      nutritionalInfo[field as keyof FeedStockNutritionalInfo] !== null
    );

    return fields.length > 0 ? (completedFields.length / fields.length) * 100 : 0;
  };

  const getSectionIcon = (section: string) => {
    switch (section) {
      case 'macronutrients':
        return <BeakerIcon className="h-5 w-5" />;
      case 'majorMinerals':
        return <CubeIcon className="h-5 w-5" />;
      case 'traceMinerals':
        return <SparklesIcon className="h-5 w-5" />;
      case 'fatSolubleVitamins':
      case 'waterSolubleVitamins':
        return <LightBulbIcon className="h-5 w-5" />;
      default:
        return <InformationCircleIcon className="h-5 w-5" />;
    }
  };

  const getCompletionColor = (percentage: number): string => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const NutritionalSection = ({ 
    title, 
    sectionKey, 
    children 
  }: { 
    title: string; 
    sectionKey: string; 
    children: React.ReactNode;
  }) => {
    const isExpanded = expandedSections.has(sectionKey);
    const completion = getCompletionPercentage(sectionKey);
    
    return (
      <div className="border border-gray-200 rounded-lg">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-inset"
        >
          <div className="flex items-center space-x-3">
            <div className="text-cyan-600">
              {getSectionIcon(sectionKey)}
            </div>
            <div>
              <h3 className="text-sm font-medium text-slate-900">{title}</h3>
              <p className="text-xs text-slate-500">
                {completion.toFixed(0)}% complete
                <span className={`ml-2 font-medium ${getCompletionColor(completion)}`}>
                  ({completion >= 80 ? 'Good' : completion >= 50 ? 'Partial' : 'Limited'})
                </span>
              </p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-slate-400" />
          )}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Macronutrients */}
      <NutritionalSection title="Macronutrients" sectionKey="macronutrients">
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Protein:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.protein, ' g/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Fat:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.fat, ' g/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Fiber:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.fiber, ' g/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Carbohydrate:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.carbohydrate, ' g/kg', 1)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Moisture:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.moisture, ' g/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Calories:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.calories, ' kcal/kg', 0)}</span>
            </div>
          </div>
        </div>
      </NutritionalSection>

      {/* Major Minerals */}
      <NutritionalSection title="Major Minerals" sectionKey="majorMinerals">
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Calcium:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.calcium, ' g/kg', 2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Phosphorus:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.phosphorus, ' g/kg', 2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Magnesium:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.magnesium, ' g/kg', 2)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Potassium:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.potassium, ' g/kg', 2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Sodium:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.sodium, ' g/kg', 2)}</span>
            </div>
          </div>
        </div>
      </NutritionalSection>

      {/* Trace Minerals */}
      <NutritionalSection title="Trace Minerals" sectionKey="traceMinerals">
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Iron:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.iron, ' mg/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Zinc:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.zinc, ' mg/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Manganese:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.manganese, ' mg/kg', 1)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Copper:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.copper, ' mg/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Selenium:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.selenium, ' mg/kg', 2)}</span>
            </div>
          </div>
        </div>
      </NutritionalSection>

      {/* Fat-Soluble Vitamins */}
      <NutritionalSection title="Fat-Soluble Vitamins" sectionKey="fatSolubleVitamins">
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Vitamin A:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.vitaminA, ' IU/kg', 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Vitamin D3:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.vitaminD3, ' IU/kg', 0)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Vitamin E:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.vitaminE, ' IU/kg', 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Vitamin K:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.vitaminK, ' mg/kg', 2)}</span>
            </div>
          </div>
        </div>
      </NutritionalSection>

      {/* Water-Soluble Vitamins */}
      <NutritionalSection title="Water-Soluble Vitamins (B-Complex)" sectionKey="waterSolubleVitamins">
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Thiamine (B1):</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.thiamine, ' mg/kg', 2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Riboflavin (B2):</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.riboflavin, ' mg/kg', 2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Niacin (B3):</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.niacin, ' mg/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Pantothenic Acid (B5):</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.pantothenicAcid, ' mg/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Pyridoxine (B6):</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.pyridoxine, ' mg/kg', 2)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Biotin:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.biotin, ' mg/kg', 3)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Folic Acid:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.folicAcid, ' mg/kg', 2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Vitamin B12:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.vitaminB12, ' mcg/kg', 1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-600">Choline:</span>
              <span className="text-sm font-medium">{formatValue(nutritionalInfo.choline, ' mg/kg', 0)}</span>
            </div>
          </div>
        </div>
      </NutritionalSection>

      {/* Analysis Metadata */}
      {showAnalysisMetadata && (
        <div className="bg-slate-50/80 rounded-lg p-4 border border-gray-200">
          <h4 className="text-sm font-medium text-slate-900 mb-3 flex items-center">
            <InformationCircleIcon className="h-4 w-4 mr-2" />
            Analysis Information
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Guaranteed Analysis:</span>
                <span className="text-sm font-medium flex items-center">
                  {nutritionalInfo.guaranteed ? (
                    <>
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                      Yes
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-1" />
                      No
                    </>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Analysis Date:</span>
                <span className="text-sm font-medium">
                  {nutritionalInfo.analysisDate 
                    ? format(new Date(nutritionalInfo.analysisDate), 'PPP')
                    : 'Not specified'
                  }
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Analysis Method:</span>
                <span className="text-sm font-medium">
                  {nutritionalInfo.analysisMethod || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Certified By:</span>
                <span className="text-sm font-medium">
                  {nutritionalInfo.certifiedBy || 'Not specified'}
                </span>
              </div>
            </div>
          </div>
          {nutritionalInfo.lastUpdated && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Last Updated:</span>
                <span className="text-sm font-medium">
                  {format(new Date(nutritionalInfo.lastUpdated), 'PPP')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 